import React, { useCallback, useMemo } from 'react';
import { useContext } from 'react';

import { flatten, uniq } from 'ramda';

import { IBlog } from '../Blog.types';
import { useSessionStorage } from './useSessionStorage';

type FilterItem = { key: string; name: string; condition: string; id: string };

interface Filters {
  authors: FilterItem[];
  tags: FilterItem[];
}

interface FilterContextProps extends Filters {
  blogs: IBlog[];
}

interface BlogFilterContext {
  appliedFiltersByType: StoredValue;
  appliedFilters: string[];
  filters: Filters;
  removeFilter: (filterType: keyof Filters, key: string) => void;
  removeAllFilters: () => void;
  addFilterTo: (filterType: keyof Filters, filter: string) => void;
  onFilterChange: (filterType: keyof Filters, filters: string[]) => void;
  removeAllFiltersFrom: (filterType: keyof Filters) => void;
  filteredBlogs: IBlog[];
}

type StoredValue = Record<keyof Filters, string[]>;

const DEFAULT_STORED_FILTERS: StoredValue = {
  authors: [],
  tags: [],
};
const SESSION_STORAGE_KEY = 'BLOG_FILTERS';
const Context = React.createContext<BlogFilterContext | null>(null);

const BlogFilterContextProvider = ({
  children,
  blogs,
  ...filters
}: React.PropsWithChildren<FilterContextProps>) => {
  const {
    storedValue: appliedFiltersByType,
    setValue,
    removeValue,
  } = useSessionStorage<StoredValue>(SESSION_STORAGE_KEY, DEFAULT_STORED_FILTERS);
  const appliedFilters = useMemo(
    () => flatten(Object.values(appliedFiltersByType)),
    [appliedFiltersByType]
  );
  const { tags, authors } = useMemo(
    () =>
      (Object.keys(appliedFiltersByType) as (keyof Filters)[]).reduce(
        (acc, key) => ({
          ...acc,
          [key]: appliedFiltersByType[key].map(
            (filterString) => filters[key].find((filter) => filter.key === filterString)?.id
          ),
        }),
        {} as StoredValue
      ),
    [appliedFiltersByType]
  );

  const onFilterChange = (filterType: keyof Filters, filters: string[]) => {
    setValue({ ...appliedFiltersByType, [filterType]: filters });
  };

  const addFilterTo = (filterType: keyof Filters, filter: string) => {
    setValue({
      ...appliedFiltersByType,
      [filterType]: uniq([...appliedFiltersByType[filterType], filter]),
    });
  };

  const removeFilter = (filterType: keyof Filters, key: string) => {
    setValue({
      ...appliedFiltersByType,
      [filterType]: appliedFiltersByType[filterType].filter((value) => value !== key),
    });
  };

  const removeAllFiltersFrom = (filterType: keyof Filters) => {
    setValue({
      ...appliedFiltersByType,
      [filterType]: [],
    });
  };

  const filterBlogs = useCallback(() => {
    const blogHasTagIfFiltered = (blog: IBlog) =>
      tags.length === 0 || blog.tags.some(({ tags_id }) => tags.includes(tags_id.id));
    const blogHasAuthorIfFilted = (blog: IBlog) =>
      authors.length === 0 ||
      blog.authors.some(({ authors_id }) => authors.includes(authors_id.id));

    const filteredBlogs = blogs.filter(
      (blog) =>
        blog.translations.length > 0 && blogHasAuthorIfFilted(blog) && blogHasTagIfFiltered(blog)
    );

    return filteredBlogs;
  }, [blogs, tags, authors]);

  return (
    <Context.Provider
      value={{
        appliedFiltersByType,
        appliedFilters,
        filters,
        filteredBlogs: filterBlogs(),
        removeFilter,
        addFilterTo,
        removeAllFilters: removeValue,
        removeAllFiltersFrom,
        onFilterChange,
      }}
    >
      {children}
    </Context.Provider>
  );
};

const useBlogFilterContext = () => useContext(Context);

export { BlogFilterContextProvider, useBlogFilterContext };
export type { Filters };
